import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Search, UserMinus, Eye, Edit, Grid3X3, List, Phone, Mail, UserPlus } from "lucide-react";
import StudentProfile from "./StudentProfile";
import AddOccupantForm from "@/components/forms/AddOccupantForm";
import { useIsMobile } from "@/hooks/use-mobile";
import { db, auth } from "../firebase/config";
import ContentLoader from "@/authentication/ContentLoader";
import { collection, onSnapshot, doc, setDoc, query, where, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";

// Define interfaces
interface Affiliation {
  role: string;
  schoolID?: string;
}

interface Student {
  id: string;
  registrationNumber: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  class: string;
  grade: string;
  bedNumber?: string;
  admissionDate?: string;
  dateOfBirth?: string;
  gender?: string;
  parentName?: string;
  parentPhone?: string;
  medicalInfo?: string;
  status?: string;
  dormitory?: string;
  behaviorScore?: number;
  attendance?: number;
  photoUrl?: string;
}

interface Teacher {
  id: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  employeeId: string;
  position: string;
  phone?: string;
  email?: string;
}

interface Dorm {
  id: string;
  name: string;
  location: string;
  capacity: number;
  occupied: number;
  available: number;
  patronTeacherId?: string | null;
  schoolID: string;
  dormUid: string;
  submittedBy: string;
  createdAt: Date;
}

interface DormOccupantsViewProps {
  dorm: Dorm;
  onBack: () => void;
  teachers: Teacher[];
}

const DormOccupantsView = ({ dorm, onBack, teachers: propTeachers }: DormOccupantsViewProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showAddOccupant, setShowAddOccupant] = useState(false);
  const [occupants, setOccupants] = useState<Student[]>([]);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(false);
  const [teachers, setTeachers] = useState<Teacher[]>(propTeachers);
  const isMobile = useIsMobile();
  const [, setIsTablet] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check for tablet size (768px to 1024px)
  useEffect(() => {
    const checkTabletSize = () => {
      setIsTablet(window.innerWidth >= 768 && window.innerWidth <= 1024);
    };
    
    checkTabletSize();
    window.addEventListener('resize', checkTabletSize);
    
    return () => window.removeEventListener('resize', checkTabletSize);
  }, []);

  // Force card view on mobile
  useEffect(() => {
    if (isMobile) {
      setViewMode('card');
    }
  }, [isMobile]);

  // Authentication and schoolID verification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          await user.getIdToken(true);
          const idTokenResult = await user.getIdTokenResult(true);
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;

          let selectedAffiliation: Affiliation | null = null;
          if (defaultAffiliation?.role === "admin" && defaultAffiliation.schoolID) {
            selectedAffiliation = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAffiliation = affiliations.find(aff => aff.role === "admin" && aff.schoolID) || affiliations[0];
          }

          if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID) {
            setSchoolID(selectedAffiliation.schoolID);
          } else {
            // Retry after delay to handle claim propagation
            await new Promise(resolve => setTimeout(resolve, 10000));
            await user.getIdToken(true);
            const retryIdTokenResult = await user.getIdTokenResult(true);
            const retryAffiliations = (retryIdTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
            const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as Affiliation | undefined;
            const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

            selectedAffiliation = retryDefaultAff?.role === "admin" && retryDefaultAff.schoolID
              ? retryDefaultAff
              : retryAffiliations.find(aff => aff.role === "admin" && aff.schoolID) || null;

            if (selectedAffiliation?.role === "admin" && selectedAffiliation.schoolID && retrySchoolID === selectedAffiliation.schoolID) {
              setSchoolID(selectedAffiliation.schoolID);
            } else {
              throw new Error("Invalid admin claims after retry");
            }
          }
        } catch (error: any) {
          console.error("Error verifying user claims:", error);
          toast({
            title: "Authentication Error",
            description: "Failed to verify permissions. Please log in again.",
            variant: "destructive",
            duration: 5000,
          });
          navigate("/login-page");
        }
      } else {
        setSchoolID(null);
        toast({
          title: "Authentication Error",
          description: "No user logged in. Please log in and try again.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, toast]);

  // Fetch teachers if propTeachers is empty and patronTeacherId exists
  const fetchTeachers = useCallback(async () => {
    if (!schoolID || !dorm.patronTeacherId || propTeachers.length > 0) {
      setIsLoadingTeachers(false);
      return;
    }

    setIsLoadingTeachers(true);
    try {
      const teachersRef = collection(db, `schools/${schoolID}/teachers`);
      const teacherSnapshot = await getDocs(teachersRef);
      const teacherList: Teacher[] = teacherSnapshot.docs.map((doc) => ({
        id: doc.id,
        firstName: doc.data().firstName || "",
        lastName: doc.data().lastName || "",
        middleName: doc.data().middleName || undefined,
        employeeId: doc.data().employeeId || "",
        position: doc.data().teacherType || "",
        phone: doc.data().phoneNumber || undefined,
        email: doc.data().email || undefined,
      }));
      setTeachers(teacherList);
      if (process.env.NODE_ENV !== "production") {
        console.log("Fetched Teachers:", teacherList);
      }
    } catch (error: any) {
      console.error("Error fetching teachers:", error);
      toast({
        title: "Error Loading Teachers",
        description: `Failed to load teacher data: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsLoadingTeachers(false);
    }
  }, [schoolID, dorm.patronTeacherId, propTeachers, toast]);

  useEffect(() => {
    if (!isLoading && schoolID && dorm.patronTeacherId && propTeachers.length === 0) {
      fetchTeachers();
    } else {
      setTeachers(propTeachers);
      setIsLoadingTeachers(false);
    }
  }, [isLoading, schoolID, dorm.patronTeacherId, propTeachers, fetchTeachers]);

  // Fetch occupants from Firestore
  useEffect(() => {
    if (!schoolID || !dorm.name) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const studentsRef = collection(db, `schools/${schoolID}/students`);
    const q = query(studentsRef, where("dormitory", "==", dorm.name));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      try {
        const studentsData: Student[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          // Use className if available, otherwise construct from grade and class
          let displayClass = data.className || "";
          if (!displayClass && data.grade && data.class) {
            displayClass = `${data.grade} ${data.class}`;
          } else if (!displayClass && data.grade) {
            displayClass = data.grade;
          }

          return {
            id: doc.id,
            registrationNumber: data.registrationNumber || "",
            firstName: data.firstName || "",
            middleName: data.middleName || "",
            lastName: data.lastName || "",
            class: displayClass,
            grade: data.grade || "",
            bedNumber: data.bedNumber || "",
            admissionDate: data.admissionDate || "",
            dateOfBirth: data.dateOfBirth || "",
            gender: data.gender || "",
            parentName: data.parentName || "",
            parentPhone: data.parentPhone || "",
            medicalInfo: data.medicalInfo || "",
            status: data.status || "",
            dormitory: data.dormitory || "",
            behaviorScore: data.behaviorScore || 0,
            attendance: data.attendance || 0,
            photoUrl: data.photoUrl || undefined,
          };
        });
        setOccupants(studentsData.sort((a, b) => a.firstName.localeCompare(b.firstName)));
      } catch (error: any) {
        console.error("Error fetching occupants:", error);
        toast({
          title: "Error Loading Occupants",
          description: `Failed to load occupants: ${error.message || "An unexpected error occurred."}`,
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setIsLoading(false);
      }
    }, (error) => {
      console.error("Error listening to occupants:", error);
      toast({
        title: "Error Loading Occupants",
        description: `Failed to load occupants: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [schoolID, dorm.name, toast]);

  // Find the patron teacher
  const patronTeacher = dorm.patronTeacherId
    ? teachers.find(teacher => teacher.id === dorm.patronTeacherId)
    : null;

  // Debug logging for patron teacher
  if (process.env.NODE_ENV !== "production") {
    console.log("Dorm PatronTeacherId:", dorm.patronTeacherId);
    console.log("Teachers Array:", teachers);
    console.log("Selected Patron Teacher:", patronTeacher);
  }

  // Handle removing an occupant
  const handleRemoveOccupant = async (studentId: string) => {
    if (!schoolID) {
      toast({
        title: "Authentication Error",
        description: "School ID missing. Please log in again.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    try {
      await setDoc(doc(db, `schools/${schoolID}/students`, studentId), {
        dormitory: null,
        bedNumber: null,
      }, { merge: true });
      toast({
        title: "Occupant Removed",
        description: "Student has been removed from the dormitory.",
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Error removing occupant:", error);
      toast({
        title: "Error Removing Occupant",
        description: `Failed to remove occupant: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  // Handle adding an occupant
  const handleAddOccupant = async (registrationNumber: string) => {
    if (!schoolID) {
      toast({
        title: "Authentication Error",
        description: "School ID missing. Please log in again.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    if (dorm.available <= 0) {
      toast({
        title: "Dormitory Full",
        description: "This dormitory has no available capacity.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    try {
      // Find student by registration number
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const q = query(studentsRef, where("registrationNumber", "==", registrationNumber));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        toast({
          title: "Student Not Found",
          description: `No student found with registration number ${registrationNumber}.`,
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      const studentDoc = snapshot.docs[0];
      const studentData = studentDoc.data();

      if (studentData.dormitory && studentData.dormitory !== dorm.name) {
        toast({
          title: "Student Already Assigned",
          description: `This student is already assigned to ${studentData.dormitory}.`,
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      // Assign student to dormitory
      await setDoc(doc(db, `schools/${schoolID}/students`, studentDoc.id), {
        dormitory: dorm.name,
        bedNumber: `B${String(dorm.occupied + 1).padStart(3, '0')}`,
      }, { merge: true });

      toast({
        title: "Occupant Added",
        description: `Student ${registrationNumber} has been added to ${dorm.name}.`,
        duration: 3000,
      });
      setShowAddOccupant(false);
    } catch (error: any) {
      console.error("Error adding occupant:", error);
      toast({
        title: "Error Adding Occupant",
        description: `Failed to add occupant: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  // Handle viewing a student profile
  const handleViewStudent = (student: Student) => {
    setSelectedStudent(student);
  };

  const handleBackFromProfile = () => {
    setSelectedStudent(null);
  };

  const filteredOccupants = occupants.filter(occupant =>
    occupant.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    occupant.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    occupant.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Loading state
  /* ── Loading state is now handled by ContentLoader wrapper ──────────────── */

  // If viewing student profile
  if (selectedStudent) {
    return (
      <StudentProfile 
        student={selectedStudent} 
        onBack={handleBackFromProfile}
      />
    );
  }

  const ActionButtons = ({ occupant }: { occupant: Student }) => (
    <div className="flex flex-col gap-2 w-full">
      <Button
        size="sm"
        variant="outline"
        onClick={() => handleViewStudent(occupant)}
        className="flex items-center justify-center gap-2 text-xs h-8 w-full border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
      >
        <Eye className="h-3 w-3 flex-shrink-0" />
        <span>View</span>
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="flex items-center justify-center gap-2 text-xs h-8 w-full border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
        disabled // Transfer functionality not implemented
      >
        <Edit className="h-3 w-3 flex-shrink-0" />
        <span>Transfer</span>
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="flex items-center justify-center gap-2 text-xs h-8 w-full hover:bg-destructive/10 hover:text-destructive hover:border-destructive border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          >
            <UserMinus className="h-3 w-3 flex-shrink-0" />
            <span>Remove</span>
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Occupant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {occupant.firstName} {occupant.lastName} from {dorm.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleRemoveOccupant(occupant.id)}
              className="bg-destructive hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  const TableActionButtons = ({ occupant }: { occupant: Student }) => (
    <div className="flex gap-1 w-full justify-end">
      <Button
        size="sm"
        variant="outline"
        onClick={() => handleViewStudent(occupant)}
        className="flex items-center gap-1 text-xs px-3 py-1 h-8 min-w-[70px] border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
      >
        <Eye className="h-3 w-3" />
        <span>View</span>
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="flex items-center gap-1 text-xs px-3 py-1 h-8 min-w-[80px] border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
        disabled // Transfer functionality not implemented
      >
        <Edit className="h-3 w-3" />
        <span>Transfer</span>
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="flex items-center gap-1 text-xs px-3 py-1 h-8 min-w-[80px] hover:bg-destructive/10 hover:text-destructive hover:border-destructive border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          >
            <UserMinus className="h-3 w-3" />
            <span>Remove</span>
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Occupant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {occupant.firstName} {occupant.lastName} from {dorm.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleRemoveOccupant(occupant.id)}
              className="bg-destructive hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  return (
    <ContentLoader isLoading={isLoading || isLoadingTeachers} message={isLoadingTeachers ? "Loading teachers..." : "Loading occupants..."}>
      <div className="w-full h-full bg-gray-50 dark:bg-gray-900">
        <div className="p-0">
            <div className="mb-6">
              <Button 
                onClick={onBack} 
                variant="outline" 
                className="mb-4 flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Dormitories
              </Button>
              
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {dorm.name} Occupants
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400">
                    {dorm.location} • Capacity: {dorm.capacity}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="secondary" className="bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white">
                      {dorm.occupied} Occupied
                    </Badge>
                    <Badge variant="outline" className="border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                      {dorm.available} Available
                    </Badge>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => setShowAddOccupant(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700"
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>Add Occupant</span>
                  </Button>
                  {!isMobile && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setViewMode('card')}
                        variant={viewMode === 'card' ? 'default' : 'outline'}
                        size="sm"
                        className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                      >
                        <Grid3X3 className="h-4 w-4" />
                        <span className="hidden sm:inline">Cards</span>
                      </Button>
                      <Button
                        onClick={() => setViewMode('table')}
                        variant={viewMode === 'table' ? 'default' : 'outline'}
                        size="sm"
                        className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                      >
                        <List className="h-4 w-4" />
                        <span className="hidden sm:inline">Table</span>
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {(patronTeacher || process.env.NODE_ENV === 'development') ? (
                <div className="mb-4">
                  <Card className="border-none bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-sm">
                            {patronTeacher?.firstName?.[0] || 'P'}{patronTeacher?.lastName?.[0] || 'P'}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700 text-xs font-medium">
                              DORM PATRON
                            </Badge>
                          </div>
                          <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                            {patronTeacher ? `${patronTeacher.firstName} ${patronTeacher.middleName || ''} ${patronTeacher.lastName}` : 'No Patron Assigned'}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {patronTeacher ? `${patronTeacher.employeeId || 'N/A'} • ${patronTeacher.position.charAt(0).toUpperCase() + patronTeacher.position.slice(1)}` : 'N/A'}
                          </p>
                        </div>
                        {patronTeacher && (
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-2 text-xs border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                              onClick={() => window.open(`tel:${patronTeacher.phone || ''}`)}
                              disabled={!patronTeacher.phone}
                            >
                              <Phone className="h-3 w-3" />
                              <span className="hidden sm:inline">Call</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-2 text-xs border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                              onClick={() => window.open(`mailto:${patronTeacher.email || ''}`)}
                              disabled={!patronTeacher.email}
                            >
                              <Mail className="h-3 w-3" />
                              <span className="hidden sm:inline">Email</span>
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex flex-col sm:flex-row gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-green-600 dark:text-green-400" />
                          <span>{patronTeacher?.phone || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-green-600 dark:text-green-400" />
                          <span className="truncate">{patronTeacher?.email || 'N/A'}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <div className="mb-4">
                  <Card className="border-none bg-gradient-to-r from-red-50 to-red-100 dark:from-red-950 dark:to-red-900">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-sm">N/A</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700 text-xs font-medium">
                              DORM PATRON
                            </Badge>
                          </div>
                          <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                            No Patron Assigned
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {dorm.patronTeacherId
                              ? `Teacher with ID ${dorm.patronTeacherId} not found. Please assign a valid patron teacher.`
                              : 'Please assign a patron teacher to this dormitory.'}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                </div>
              )}

              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
                <Input
                  placeholder="Search occupants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {(isMobile || viewMode === 'card') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {filteredOccupants.map((occupant) => (
                  <Card key={occupant.id} className="border-none bg-white dark:bg-gray-800 hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden border border-purple-200">
                            {occupant.photoUrl ? (
                              <img src={occupant.photoUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-purple-600 dark:text-purple-300 font-semibold">
                                {occupant.firstName.charAt(0)}{occupant.lastName.charAt(0)}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                              {occupant.firstName} {occupant.lastName}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                              {occupant.registrationNumber}
                            </p>
                          </div>
                        </div>
                        <Badge variant="secondary" className="bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white ml-2 flex-shrink-0">
                          {occupant.bedNumber || 'N/A'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm mb-4">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Class:</span>
                          <p className="text-gray-900 dark:text-white">{occupant.class || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Admission Date:</span>
                          <p className="text-gray-900 dark:text-white">{occupant.admissionDate || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <ActionButtons occupant={occupant} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!isMobile && viewMode === 'table' && (
              <Card className="border-none bg-white dark:bg-gray-800">
                <div className="overflow-x-auto">
                  <Table className="min-w-full">
                    <TableHeader>
                      <TableRow className="border-b-2">
                        <TableHead className="h-12 border-r font-semibold px-4 w-12"></TableHead>
                        <TableHead className="h-12 border-r font-semibold px-4 min-w-[160px]">Name</TableHead>
                        <TableHead className="h-12 border-r font-semibold px-4 min-w-[120px]">Registration</TableHead>
                        <TableHead className="h-12 border-r font-semibold px-4 min-w-[120px]">Class</TableHead>
                        <TableHead className="h-12 border-r font-semibold px-4 w-20">Bed</TableHead>
                        <TableHead className="h-12 border-r font-semibold px-4 min-w-[110px]">Admission</TableHead>
                        <TableHead className="text-center h-12 font-semibold px-4 min-w-[280px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredOccupants.map((occupant, index) => (
                        <TableRow 
                          key={occupant.id} 
                          className={`border-b h-12 ${
                            index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900' : 'bg-gray-100 dark:bg-gray-800'
                          } hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors`}
                        >
                          <TableCell className="border-r p-4">
                            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center overflow-hidden border border-purple-200">
                                {occupant.photoUrl ? (
                                  <img src={occupant.photoUrl} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-purple-600 dark:text-purple-300 font-semibold text-xs">
                                    {occupant.firstName.charAt(0)}{occupant.lastName.charAt(0)}
                                  </span>
                                )}
                              </div>
                          </TableCell>
                          <TableCell className="border-r p-4">
                            <div className="text-sm font-medium leading-tight text-gray-900 dark:text-white">
                              {occupant.firstName} {occupant.lastName}
                            </div>
                          </TableCell>
                          <TableCell className="border-r p-4 text-sm text-gray-900 dark:text-white">
                            {occupant.registrationNumber}
                          </TableCell>
                          <TableCell className="border-r p-4 text-sm text-gray-900 dark:text-white">
                            {occupant.class || 'N/A'}
                          </TableCell>
                          <TableCell className="border-r p-4">
                            <Badge variant="secondary" className="bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white text-xs px-2 py-0.5">
                              {occupant.bedNumber || 'N/A'}
                            </Badge>
                          </TableCell>
                          <TableCell className="border-r p-4 text-sm text-gray-900 dark:text-white">
                            {occupant.admissionDate || 'N/A'}
                          </TableCell>
                          <TableCell className="p-4">
                            <TableActionButtons occupant={occupant} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            )}

            {filteredOccupants.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600 dark:text-gray-400">No occupants found in this dormitory.</p>
              </div>
            )}

            <AddOccupantForm
              isOpen={showAddOccupant}
              onClose={() => setShowAddOccupant(false)}
              onAddOccupant={handleAddOccupant}
              dormName={dorm.name}
            />
        </div>
      </div>
    </ContentLoader>
  );
};

export default DormOccupantsView;